import { useCallback } from 'react'
import { dataService, type ShopifyCustomer, type ShopifyProduct } from '../../services/dataService'
import { scopeKey, usePagedRead, type PagedState } from './usePagedRead'
import { Button, LoadMore } from '../../ds'

/**
 * The Knowledge index: the two paged `query-dynamodb` lists that search reaches, and the
 * sentence that says how far that is. Moved out of the old `screens/Memory.tsx` (MAR-325)
 * unchanged in behaviour — `screens/Knowledge.tsx` is now its only caller.
 *
 * ── The search box says what it reaches ─────────────────────────────────────────────────
 *
 * ⚠️ **Search never reaches DynamoDB.** `query-dynamodb` applies `search` in memory AFTER
 * the page is fetched (`services/data/query-dynamodb/src/index.js:903-913`), because email
 * is encrypted at rest and cannot be a DynamoDB filter without decrypting every row;
 * `Limit` is clamped to 200 (`:829`). So a search reaches exactly the page that was
 * loaded — server-side or client-side, it is the same rows. The screen therefore filters
 * locally and does NOT send `search`: sending it would narrow the page after the fetch and
 * leave the loaded count and the cursor describing two different sets.
 *
 * ⚠️ **`pagination.total` is a fiction and nothing here may render it.**
 * `offset + filteredItems.length + (LastEvaluatedKey ? 1 : 0)` (`:930`) — on a `limit: 1`
 * probe it evaluated to **2** for every table with any row in it at all. `unwrap` below
 * deliberately does not carry the field out of the response, so putting it back is an
 * edit to this file rather than an accident at a call site.
 *
 * The one EXACT count this module can give is `items.length` once the server returned no
 * cursor (`hasMore: false`) — the one moment the loaded list provably is the workspace.
 * `exactCount` is that rule, and it is what the page's scale line and scope chips print.
 */

/**
 * How many rows one page asks for.
 *
 * The server clamps `Limit` to 200 and applies `search` after the fetch, so this number is
 * literally how far a search can see before the merchant loads more. 100 is a page whose
 * size the merchant can be told; a larger one would not make the claim any truer.
 */
export const PAGE_SIZE = 100

export function fmtCurrency(n: number) {
  return new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP', maximumFractionDigits: 0 }).format(n || 0)
}

export function timeAgo(ts: number) {
  const s = Math.max(0, Math.floor((Date.now() - ts) / 1000))
  if (s < 5) return 'now'
  if (s < 60) return `${s}s`
  const m = Math.floor(s / 60)
  if (m < 60) return `${m}m`
  const h = Math.floor(m / 60)
  if (h < 24) return `${h}h`
  return `${Math.floor(h / 24)}d`
}

/** A lifecycle reading of one person, for that person's own row. Never a filter. */
export type Lifecycle = 'vip' | 'loyal' | 'risk' | 'new'

/**
 * How one person's row reads, from that person's own record.
 *
 * ⚠️ This SURVIVED the chip removal deliberately (MAR-204 PR 3, §9.3): rendering
 * `lifecycleStage` on the row it belongs to is a fact about that person; assembling people
 * into an audience by it is the segment list, and that is what went.
 *
 * ⚠️ `null` when the record carries no `customerIntelligence` at all — an index row (list
 * queries omit the blob) or a record whose blob was never written. That is a stage NOBODY
 * READ, and it must not render as "New": a VIP opened from search read "New · 12 orders".
 * "New" is said only of a record whose intelligence was read and names no other stage.
 */
export function lifecycleStyle(c: ShopifyCustomer): { cls: Lifecycle; label: string } | null {
  const ci = c.customerIntelligence
  if (!ci) return null
  if (ci?.lifecycleStage === 'vip' || ci?.valueSegment === 'vip') return { cls: 'vip', label: 'VIP' }
  if (ci?.lifecycleStage === 'loyal' || ci?.lifecycleStage === 'repeat') return { cls: 'loyal', label: 'Loyal' }
  if (ci?.lifecycleStage === 'at_risk' || ci?.lifecycleStage === 'churned' || ci?.lifecycleStage === 'win_back')
    return { cls: 'risk', label: 'At risk' }
  return { cls: 'new', label: 'New' }
}

export function custName(c: ShopifyCustomer) {
  return `${c.firstName || ''} ${c.lastName || ''}`.trim() || c.email || 'Customer'
}

/**
 * A record's own NAME, or `null` — never its address, which `custName` falls back to.
 * ⚠️ For anything that heads a drawer or labels the Ask pin: a nameless search row once put
 * the shopper's real email in the drawer header and in "Ask about …".
 */
export function recordName(c: ShopifyCustomer | null | undefined): string | null {
  if (!c) return null
  return `${c.firstName || ''} ${c.lastName || ''}`.trim() || null
}

/**
 * A product row carries its id under either spelling, and occasionally neither. One reading
 * of it, so the pin published on open and the pin released on close can never disagree
 * about which record this is.
 */
export function productKey(p: ShopifyProduct | null | undefined): string | undefined {
  return p ? p.productId || p.product_id : undefined
}

/**
 * The facts on a person's own row.
 *
 * A legacy churn score is deliberately not part of this projection (MAR-36): the page shows
 * source-backed traits and each belief's own confidence, not a fixed score about the person.
 */
export function custMeta(c: ShopifyCustomer) {
  const parts: string[] = [`${c.ordersCount || 0} order${c.ordersCount === 1 ? '' : 's'}`]
  const segs = c.customerIntelligence?.segments
  if (segs?.length) parts.push(segs.slice(0, 2).join(' · '))
  return parts.join(' · ')
}

/**
 * dataService methods are typed as QueryResult ({success,data:{items}}) but at runtime
 * return the flat payload ({items, pagination, ...}). Unwrap defensively to handle either.
 *
 * ⚠️ `pagination.total` is deliberately NOT carried out of the response (see the module
 * docblock). `hasMore` goes the same way: `pagination.hasMore` is `!!LastEvaluatedKey`, and
 * the cursor is the thing a `Load more` can actually use. A page that says there is more
 * and hands back no cursor cannot be paged, so `lastKey` is the only answer worth reading.
 */
export function unwrap<T>(r: unknown): { items: T[]; lastKey?: unknown } {
  const o = r as
    | {
        items?: T[]
        lastEvaluatedKey?: unknown
        data?: { items?: T[]; lastEvaluatedKey?: unknown }
      }
    | null
    | undefined
  if (!o) return { items: [] }
  return {
    items: o.items || o.data?.items || [],
    lastKey: o.lastEvaluatedKey ?? o.data?.lastEvaluatedKey,
  }
}

// ── The index: one page, a real cursor, and a label that states the bound ────────────────

/**
 * One index list, stamped with every dimension it was read under (`key`: kind, workspace,
 * account). The paging, the scoping at the READ and every guard are `usePagedRead`'s — one
 * implementation, shared with the live feed.
 */
export type IndexState<T> = PagedState<T>

export interface IndexHandle<T> {
  /** `null` while the first page for THIS workspace is unread or in flight. */
  list: IndexState<T> | null
  loadMore: () => void
  /** Ask again after a first page that failed. */
  retry: () => void
}

/**
 * The exact size of an index, or `null` when there is no exact number to give.
 *
 * Exact only when the first page loaded and the server returned no cursor. Unread,
 * loading, failed and "there is more" all answer `null`, and a caller prints nothing
 * rather than a floor dressed as a total.
 */
export function exactCount(list: IndexState<unknown> | null): number | null {
  if (!list || list.failed || list.hasMore) return null
  return list.items.length
}

/**
 * A paged index over one of the two `query-dynamodb` list routes.
 *
 * `enabled` defers the first read until the page renders something from it (MAR-325: a
 * search on this scope). Once read for a workspace it is not re-read when `enabled` drops,
 * so clearing the box and typing again keeps every page the merchant loaded.
 *
 * ⚠️ `search` is not sent. See the module docblock: the server filters the page after
 * fetching it, so sending it would not widen the reach by one row and would make the
 * loaded count and the cursor describe different sets.
 */
export function useIndex<T>(
  kind: 'customers' | 'products',
  workspaceId?: string,
  accountId?: string,
  enabled = true
): IndexHandle<T> {
  const fetchPage = useCallback(
    async(cursor: unknown): Promise<{ items: T[]; cursor: unknown }> => {
      const params = {
        workspaceId: workspaceId || '',
        accountId,
        limit: PAGE_SIZE,
        lastEvaluatedKey: cursor ?? undefined,
      }
      const raw =
        kind === 'customers'
          ? await dataService.queryShopifyCustomers(params)
          : await dataService.queryShopifyProducts(params)
      const r = unwrap<T>(raw)
      return { items: r.items, cursor: r.lastKey ?? null }
    },
    [kind, workspaceId, accountId]
  )
  const key = workspaceId ? scopeKey('index', kind, workspaceId, accountId) : null
  const { page, loadMore, retry } = usePagedRead<T>(key, fetchPage, enabled)
  return { list: page, loadMore, retry }
}

/** `1 customer` / `2 customers`, because the bound is often exactly one row wide. */
export function countOf(n: number, noun: 'customer' | 'product'): string {
  return `${n} ${noun}${n === 1 ? '' : 's'}`
}

/**
 * What search actually reaches, said out loud.
 *
 * ⚠️ Three claims, each of which has to be earned:
 *  - the NUMBER is what is loaded in this browser, never `pagination.total`;
 *  - the ORDER is per page, because the handler sorts each page it fetched and the GSI is
 *    keyed on `accountId`/`workspaceId` — there is no global ordering to promise;
 *  - "all of them" is claimed ONLY when the server returned no cursor, which is the one
 *    moment the list provably is the workspace.
 */
export function searchBound(noun: 'customer' | 'product', loaded: number, hasMore: boolean): string {
  const plural = `${noun}s`
  if (loaded === 0) {
    return hasMore
      ? `No ${plural} have loaded yet, so there is nothing here to search. Load more to reach further in.`
      : `There are no ${plural} in this workspace, so there is nothing here to search.`
  }
  if (hasMore) {
    return `Search reaches the ${countOf(loaded, noun)} loaded here, newest-updated first within each page — not the rest of this workspace.`
  }
  return `Search reaches all ${countOf(loaded, noun)} in this workspace. There are no more to load.`
}

/**
 * The bound, the way past it, and the retry.
 *
 * "Ask her" is an invitation, not a capability claim: the dock is where a question about
 * someone who is not on this page belongs, and this copy deliberately does not promise
 * that she will find them.
 */
export function IndexBound({
  noun,
  list,
  onLoadMore,
  onAsk,
}: {
  noun: 'customer' | 'product'
  list: IndexState<unknown> | null
  onLoadMore: () => void
  onAsk: () => void
}) {
  if (!list || list.failed) return null
  const sentence = (
    <>
      <span data-testid="kn-bound-l">{searchBound(noun, list.items.length, list.hasMore)}</span>
      {list.moreFailed && (
        <>
          {' '}
          <span data-testid="kn-bound-failed">
            That did not load, so nothing was added rather than a partial page. The {countOf(list.items.length, noun)}{' '}
            above are unaffected — try again.
          </span>
        </>
      )}{' '}
      <Button variant="link" onClick={onAsk}>
        {noun === 'customer' ? 'Someone not here? Ask her' : 'Something not here? Ask her'} (⌘K)
      </Button>
    </>
  )
  // The sentence rides the LoadMore's note while there is more to load, and its end line once
  // there is not — it is said either way (see `searchBound`).
  return (
    <div data-testid="kn-bound">
      <LoadMore
        hasMore={list.hasMore}
        onLoad={onLoadMore}
        state={list.loadingMore ? 'loading' : list.moreFailed ? 'error' : 'idle'}
        label="Load more"
        note={list.hasMore ? sentence : undefined}
        shown={list.hasMore ? undefined : sentence}
      />
    </div>
  )
}

// ── Small shared pieces ─────────────────────────────────────────────────────────────────

export const ICON: Record<string, string> = {
  search: 'M10.5 17a6.5 6.5 0 1 0 0-13 6.5 6.5 0 0 0 0 13z|M15.5 15.5L20 20',
  view: 'M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7z|M12 10a2 2 0 1 0 0 4 2 2 0 0 0 0-4z',
  cart: 'M5 6h16l-1.5 9h-12zM5 6L4 3H2',
  session: 'M12 7v5l3 2|M12 3a9 9 0 1 0 0 18 9 9 0 0 0 0-18z',
  purchase: 'M5 12.5l4.5 4.5L19 7',
}

